import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  PLATFORM_ID,
  inject,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import {
  Event,
  EventsService,
  Subevento,
} from '../../core/services/events.service';
import { MetaService } from '../../core/services/meta.service';
import { WebAppConfigService } from '../../core/services/webapp-config.service';
import { Header } from '../../components/header/header';
import { Footer } from '../../components/footer/footer';
import { ScrollToTop } from '../../components/scroll-to-top/scroll-to-top';
import { TicketSelectorTren } from '../../components/ticket-selector-tren/ticket-selector-tren';
import { TicketSelector } from '../../components/ticket-selector/ticket-selector';
import { TicketsService, TipoTicketDetailed } from '../../core/services/tickets.service';

@Component({
  selector: 'app-comprar-vagon',
  standalone: true,
  imports: [CommonModule, Header, Footer, ScrollToTop, TicketSelectorTren, TicketSelector],
  templateUrl: './comprar-vagon.html',
  styleUrl: './comprar-vagon.css',
})
export class ComprarVagonPage implements OnInit, OnDestroy {
  @ViewChild(Header) header?: Header;

  event?: Event;
  subevento?: Subevento;
  tipoticket?: TipoTicketDetailed;
  fecha = '';
  hora = '';
  tipoticketid = '';
  isLoading = true;
  error = false;
  isExpired = false;
  expirationMessage = '';

  private platformId = inject(PLATFORM_ID);
  private isBrowser: boolean;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private eventsService: EventsService,
    private ticketsService: TicketsService,
    private metaService: MetaService,
    private webAppConfig: WebAppConfigService,
    private cdr: ChangeDetectorRef
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    const config = this.webAppConfig.config();
    if (config) {
      this.metaService.resetToDefault(
        this.webAppConfig.getSiteName() + ' - ' + config.short_description,
        config.description,
        config.url_logo,
        config.url
      );
    }

    this.route.params.subscribe(params => {
      const urlSlug = params['url_slug'];
      const slugActividad = params['slug_actividad'];
      this.tipoticketid = params['tipoticketid'] || '';
      this.route.queryParams.subscribe(qp => {
        this.fecha = qp['fecha'] || '';
        this.hora = qp['hora'] || '';
        if (urlSlug && slugActividad && this.tipoticketid) {
          this.loadData(urlSlug, slugActividad);
        } else {
          this.router.navigate(['/evento', urlSlug, 'actividad', slugActividad]);
        }
      });
    });
  }

  ngOnDestroy(): void {
    const config = this.webAppConfig.config();
    if (config) {
      this.metaService.resetToDefault(
        this.webAppConfig.getSiteName() + ' - ' + config.short_description,
        config.description,
        config.url_logo,
        config.url
      );
    }
  }

  private loadData(urlSlug: string, slugActividad: string): void {
    this.isLoading = true;
    this.error = false;

    this.eventsService.getEventBySlug(urlSlug).subscribe({
      next: eventRes => {
        if (!eventRes.success || !eventRes.data) {
          this.error = true;
          this.isLoading = false;
          this.cdr.markForCheck();
          return;
        }
        this.event = eventRes.data;
        console.log('🎟️ [DEBUG] Event loaded:', this.event);
        console.log('🎟️ [DEBUG] tipoevento:', this.event.tipoevento, '| max_tickets_por_compra:', this.event.max_tickets_por_compra);

        this.eventsService.getSubeventoBySlug(this.event.eventoid, slugActividad).subscribe({
          next: subRes => {
            if (!subRes.success || !subRes.data) {
              this.error = true;
              this.isLoading = false;
              this.cdr.markForCheck();
              return;
            }
            this.subevento = subRes.data;

            this.ticketsService.getTipoTicketByIdForWeb(this.tipoticketid).subscribe({
              next: ttRes => {
                if (ttRes.success && ttRes.data) {
                  this.tipoticket = ttRes.data;
                  
                  // Verificar si ya expiró la venta web
                  this.checkAvailability();
                } else {
                  this.router.navigateByUrl('/404');
                  return;
                }
                this.isLoading = false;
                this.cdr.markForCheck();
              },
              error: () => {
                this.router.navigateByUrl('/404');
              }
            });
          },
          error: () => {
            this.error = true;
            this.isLoading = false;
            this.cdr.markForCheck();
          }
        });
      },
      error: () => {
        this.error = true;
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  onTicketsAdded(): void {
    if (this.header) {
      this.header.loadCartCount();
    }
  }

  goBackToVagones(): void {
    if (this.event && this.subevento) {
      this.router.navigate(
        ['/evento', this.event.url_slug, 'actividad', this.subevento.slug, 'comprar'],
        { queryParams: { fecha: this.fecha, hora: this.hora } }
      );
    }
  }

  goBackToActividad(): void {
    if (this.event && this.subevento) {
      this.router.navigate(['/evento', this.event.url_slug, 'actividad', this.subevento.slug]);
    }
  }

  goBackToEvent(): void {
    if (this.event) {
      this.router.navigate(['/evento', this.event.url_slug]);
    } else {
      this.router.navigate(['/']);
    }
  }

  getBannerImage(): string {
    // Usar exclusivamente la imagen horizontal del subevento
    if (this.subevento?.imagenes?.horizontal) return this.subevento.imagenes.horizontal;
    return 'https://dummyimage.com/2000x576/cccccc/666666?text=Comprar';
  }

  getActividadImage(): string {
    // Imagen de actividad para mostrar en la card de coche seleccionado
    if (this.subevento?.imagenes?.actividad) return this.subevento.imagenes.actividad;
    return '';
  }

  formatFecha(fecha: string): string {
    if (!fecha) return '';
    const [yyyy, mm, dd] = fecha.split('-');
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return d.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  }

  /**
   * Verifica si el ticket todavía está disponible para venta web
   * comparando la fecha actual con fecha_funcion_maxima_disponible_web (UTC)
   */
  private checkAvailability(): void {
    if (!this.tipoticket?.funcion?.fecha_funcion_maxima_disponible_web) {
      return;
    }

    const fechaMaximaUTC = new Date(this.tipoticket.funcion.fecha_funcion_maxima_disponible_web);
    const fechaActual = new Date();

    console.log('🕐 Verificando disponibilidad:');
    console.log('  - Fecha máxima venta web (UTC):', fechaMaximaUTC.toISOString());
    console.log('  - Fecha actual:', fechaActual.toISOString());

    if (fechaActual > fechaMaximaUTC) {
      this.isExpired = true;
      
      // Formatear la fecha de expiración para mostrarla
      const fechaFormateada = fechaMaximaUTC.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
      
      this.expirationMessage = `La venta web para esta función finalizó el ${fechaFormateada}.`;
      console.log('❌ Función expirada para venta web');
    } else {
      this.isExpired = false;
      console.log('✅ Función disponible para venta web');
    }
  }
}
